import { Scale } from './../../../core/models/scale.model';
import {
  ScheduleData,
  WEEK,
  WorkScheduleModel,
  ErrorItem,
  ScheduleModel,
  ScheduleModelFront,
  ScheduleError
} from '../../../core/models/scale.model';
import { Component, OnInit, Input, Output, EventEmitter, OnDestroy } from '@angular/core';
import { SnackbarService } from '../snackbar/service/snackbar.service';
import { WindowScrollService } from 'src/app/core/services/window-scroll.service';
import { ScaleService } from './service/modal-work-schedule.service';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-modal-work-schedule',
  templateUrl: './modal-work-schedule.component.html',
  styleUrls: ['./modal-work-schedule.component.scss']
})
export class ModalWorkScheduleComponent implements OnInit, OnDestroy {

  @Input() display: boolean;
  @Input() members: any[];

  @Output() closeModal: EventEmitter<null> = new EventEmitter();
  baseDay = new Date();
  showComplementaryMessage = false;
  form: WorkScheduleModel[];
  jsonFormBase: string;
  showAlertError: boolean;
  alertType: string;
  errors = {};

  constructor(
    private snack: SnackbarService,
    private windowService: WindowScrollService,
    private service: ScaleService,
  ) { }

  ngOnInit() {
    this.useMock();
    this.windowService.disable();
    if (this.members.length === 1) {
      const [member] = this.members;
      const observer = this.service.getScale(member.user_id).subscribe(response => {
        const currentWeek = [...this.form];
        response.scales.forEach(day => {
          const index = this.form.findIndex(el => el.id === day.weekday);
          const found = this.form[index];
          const formatted = this.formatScale(day, found);
          currentWeek.splice(index, 1, formatted);
        });
        this.form = currentWeek;
        this.jsonFormBase = JSON.stringify(currentWeek);
        observer.unsubscribe();
      });
    }
  }

  ngOnDestroy() {
    this.form = [];
    this.windowService.enable();
  }

  formatScale(day: Scale, currentDay: WorkScheduleModel) {
    if (currentDay) {
      const newScaleSchedule: WorkScheduleModel = {
        id: day.weekday,
        label: currentDay.label,
        schedule: day.schedules.map(schedule => {
          return { start: schedule.entry_time, end: schedule.exit_time };
        })
      }
      return newScaleSchedule;
    }
  }

  useMock(): void {
    const form = [
      { label: 'Segunda-feira', id: WEEK.MONDAY, schedule: [{ start: null, end: null }] },
      { label: 'Terça-feira', id: WEEK.TUESDAY, schedule: [{ start: null, end: null }] },
      { label: 'Quarta-feira', id: WEEK.WEDNESDAY, schedule: [{ start: null, end: null }] },
      { label: 'Quinta-feira', id: WEEK.THURSDAY, schedule: [{ start: null, end: null }] },
      { label: 'Sexta-feira', id: WEEK.FRIDAY, schedule: [{ start: null, end: null }] },
      { label: 'Sábado', id: WEEK.SATURDAY, schedule: [{ start: null, end: null }] },
      { label: 'Domingo', id: WEEK.SUNDAY, schedule: [{ start: null, end: null }] }
    ];
    this.form = form;
    this.jsonFormBase = JSON.stringify(form);
  }

  getError(dayIdx: number, schIdx: number) {
    if (!this.errors[dayIdx] || !this.errors[dayIdx][schIdx]) { return {}; }
    return this.errors[dayIdx][schIdx];
  }

  public addSchedule(day: number): void {
    this.form[day].schedule.push({ start: null, end: null });
  }

  public removeSchedule(day: number, idx: number): void {
    this.form[day].schedule.splice(idx, 1);
    const errorsAfterDeleted = (this.errors[day] && Object.keys(this.errors[day]).filter(el => Number(el) >= idx)) || [];
    errorsAfterDeleted.forEach(key => delete this.errors[day][key]);
    if (this.form[day].schedule.length === 0) {
      this.addSchedule(day);
    }
    this.revalidateAllSchedules(day);
  }

  revalidateAllSchedules(day: number): any {
    const errors = this.validate(day);
    const schedules = this.form[day].schedule;
    const currentErrors = { ...errors[day] };
    Object.keys(currentErrors).forEach((key) => {
      if (!schedules[key]) { delete errors[day][key]; }
    });
    this.errors = errors;
    return errors;
  }

  filterSchedulesWithValues(dayIdx: number, schIdx: number) {
    return this.form[dayIdx].schedule.filter((value, i) => {
      const hasValues = (value.start?.length || value.end?.length);
      return i !== schIdx && hasValues;
    });
  }

  getErrorMessage(error: boolean | string) {
    return typeof error === 'boolean' ? 'Horário dentro de período já existente.' : 'Necessário informar um intervalo de horário válido.';
  }

  mergeErrors(errors, currentDayErrors, news, dayIdx: number, i: number) {
    currentDayErrors[i] = { ...currentDayErrors[i], ...news[i] };
    errors[dayIdx] = currentDayErrors;
    return errors;
  }

  validate(dayIdx: number): any {
    const currentDayErrors = this.errors[dayIdx] || {};
    let errors = Object.assign({}, { ...this.errors });
    this.form[dayIdx].schedule.forEach((item, i) => {
      if (item.start?.length || item.end?.length) {
        const schedulesStart = this.filterSchedulesWithValues(dayIdx, i);
        const toUpdate = this.validateSchedules(dayIdx, i, 'start', errors, schedulesStart) || {};
        errors = this.mergeErrors(errors, currentDayErrors, toUpdate, dayIdx, i);
        const schedulesEnd = this.filterSchedulesWithValues(dayIdx, i);
        const endToUpdate = this.validateSchedules(dayIdx, i, 'end', errors, schedulesEnd) || {};
        errors = this.mergeErrors(errors, currentDayErrors, endToUpdate, dayIdx, i);
      }
    });
    return errors;
  }

  selectStartTime(event: string, dayIdx: number, schIdx: number) {
    this.form[dayIdx].schedule[schIdx].start = event;
    this.revalidateAllDays(false);
  }

  selectEndTime(event: string, dayIdx: number, schIdx: number): void {
    this.form[dayIdx].schedule[schIdx].end = event;
    this.revalidateAllDays(false);
  }

  isTimeAlreadyUsedInLastDay(dayIdx: number, schIdx: number, type = 'end'): number {
    if (dayIdx - 1 >= 0) {
      const variantDay = dayIdx - 1;
      return this.validateSchedulesTimes(variantDay, schIdx, type, this.form[variantDay].schedule, dayIdx);
    }
    return -1;
  }

  isTimeAlreadyUsedInNextDay(dayIdx: number, schIdx: number, type = 'end'): number {
    const variantDay = dayIdx + 1;
    if (variantDay >= 0 && variantDay < this.form.length) {
      return this.validateSchedulesTimes(variantDay, schIdx, type, this.form[variantDay].schedule, dayIdx);
    }
    return -1;
  }

  validateSchedules(dayIdx: number, schIdx: number, type = 'end', errors: object, schedules: ScheduleModel[]): ErrorItem {
    const foundIndex = this.validateSchedulesTimes(dayIdx, schIdx, type, schedules, dayIdx);
    const indexOfLastDay = this.isTimeAlreadyUsedInLastDay(dayIdx, schIdx, type);
    const indexOfNextDay = this.isTimeAlreadyUsedInNextDay(dayIdx, schIdx, type);
    const isDuplicated = foundIndex !== -1;
    const isDuplicatedInLastDay = indexOfLastDay !== -1;
    const isDuplicatedInNextDay = indexOfNextDay !== -1;
    const currentDay: ErrorItem = { ...errors[dayIdx] };
    currentDay[schIdx] = { ...currentDay[schIdx], [type]: isDuplicated || isDuplicatedInLastDay || isDuplicatedInNextDay };
    if (isDuplicated) {
      currentDay[foundIndex] = { ...currentDay[foundIndex], [type]: null };
    }

    const value = this.form[dayIdx].schedule[schIdx];
    if (value?.start && value?.end && value.start === value.end) {
      currentDay[schIdx].start = true;
      currentDay[schIdx].end = true;
    }
    return currentDay;
  }

  validateSchedulesTimes(variantDay: number, schedule: number, type = 'end', schedules: ScheduleModel[], baseDay: number): number {
    const toValidate = this.form[baseDay].schedule[schedule];
    const validateCurrentTime = this.validateTime(toValidate, type, variantDay, baseDay);
    return schedules.findIndex(validateCurrentTime);
  }

  setDateHours(value: string, indexDay: number): Date {
    if (!value || !value.length) { return null; }
    const regex = new RegExp(/(?<h>\d{1,}):(?<m>\d{1,})/);
    const regexTime = regex.exec(value);
    const { h = '', m = '' } = regexTime && regexTime.groups;
    const date = new Date(this.baseDay);
    date.setDate(date.getDate() + indexDay);
    date.setHours(Number(h), Number(m), 0, 0);
    return date;
  }

  endsInAnotherDay(value: ScheduleModel) {
    return value.start && value.end && value.start > value.end;
  }

  validateTime = (current: ScheduleModel, type = 'end', variantDay: number, baseDay: number) => (el: ScheduleModel): boolean => {
    const dayEnd = this.endsInAnotherDay(current) ? baseDay + 1 : baseDay;
    const curr: ScheduleModelFront = {
      start: this.setDateHours(current.start, baseDay),
      end: this.setDateHours(current.end, dayEnd)
    };
    const variantDayEnd = this.endsInAnotherDay(el) ? variantDay + 1 : variantDay;
    const variant: ScheduleModelFront = {
      start: this.setDateHours(el.start, variantDay),
      end: this.setDateHours(el.end, variantDayEnd)
    };
    if (type === 'start') {
      return Boolean((variant.start && curr.start && (curr.start >= variant.start)) && curr.start <= variant.end);
    }
    const isBetween = Boolean(curr.end <= variant.end && ((curr.end >= variant.start) && variant.start));
    const isGreatherThan = Boolean(curr.start <= variant.start && ((curr.end >= variant.end) && variant.end && curr.start));
    return isBetween || isGreatherThan;
  }

  hasFieldsWithErrors() {
    let hasDefaultMessage = false;
    const hasErrors = Object.values(this.errors).some(day => {
      return Object.values(day).some((item: ScheduleError) => {
        const validate = value => typeof value === 'boolean' && value;
        if (validate(item.start) || validate(item.end)) {
          hasDefaultMessage = true;
        }
        return item.start || item.end;
      });
    });
    return { hasErrors, hasDefaultMessage };
  }

  buildSchedules(schedules: ScheduleModel[]): ScheduleData[] {
    return schedules.reduce((acc, schedule) => {
      if (schedule.start && schedule.end) {
        acc.push({ entry_time: schedule.start, exit_time: schedule.end });
      }
      return acc;
    }, []);
  }

  buildData() {
    return this.form
      .reduce((acc, day) => {
        const schedules = this.buildSchedules(day.schedule);
        if (schedules.length) {
          acc.push({ weekday: day.id, schedules });
        }
        return acc;
      }, []);
  }

  saveSuccess(observer: Subscription) {
    this.snack.show('Sua solicitação está sendo processada.', 'info');
    observer.unsubscribe();
    this.onClose();
  }

  validateIncompleteTime(schedule: ScheduleModel, dayIdx: number, schIdx: number, errors: object) {
    const { end, start } = schedule;
    if (!errors[dayIdx]) { errors[dayIdx] = {}; }
    if (start && !end) {
      errors[dayIdx][schIdx] = { ...errors[dayIdx][schIdx], end: 'Nescessário informar um intervalo de horário válido.' };
    }
    if (!start && end) {
      errors[dayIdx][schIdx] = { ...errors[dayIdx][schIdx], start: 'Nescessário informar um intervalo de horário válido.' };
    }
  }

  revalidateAllDays(validateEmptyFields = true) {
    const errors = this.errors;
    this.form.forEach((day, dayIdx) => {
      const currentDayErrors = this.errors[dayIdx] || {};
      day.schedule.forEach((scheduleItem, schIdx) => {
        const schedules = day.schedule.filter((_, i) => i !== schIdx);
        const toUpdate = this.validateSchedules(dayIdx, schIdx, 'start', errors, schedules) || {};
        this.mergeErrors(errors, currentDayErrors, toUpdate, dayIdx, schIdx);
        const endToUpdate = this.validateSchedules(dayIdx, schIdx, 'end', errors, schedules) || {};
        this.mergeErrors(errors, currentDayErrors, endToUpdate, dayIdx, schIdx);
        if (scheduleItem && validateEmptyFields) {
          this.validateIncompleteTime(scheduleItem, dayIdx, schIdx, errors);
        }
      });
    });
  }

  save() {
    if (!this.dataWasTouched()) { return; }
    this.alertType = 'validation';
    this.revalidateAllDays();
    const resultValidation = this.hasFieldsWithErrors();
    if (resultValidation.hasErrors) {
      this.showComplementaryMessage = resultValidation.hasDefaultMessage;
      return this.showAlertError = true;
    }
    if (this.members.length === 1) {
      const [member] = this.members;
      const data = { user_id: member.id, scales: this.buildData() };
      const observer = this.service.saveScale(data).subscribe(() => {
        this.saveSuccess(observer);
      });
    }
    if (this.members.length > 1) {
      const userIds = this.members.map(member => member.id);
      const data = { user_ids: userIds, scales: this.buildData() };
      const observer = this.service.saveScales(data).subscribe(() => {
        this.saveSuccess(observer);
      });
    }
  }

  dataWasTouched(): boolean {
    const jsonForm = JSON.stringify(this.form);
    return jsonForm !== this.jsonFormBase;
  }

  beforeClose() {
    if (!this.dataWasTouched()) {
      this.onClose();
    }
    this.alertType = 'leaveModal';
    this.showAlertError = true;
  }

  onClose() {
    this.errors = {};
    this.closeModal.emit();
    this.useMock();
  }
}
